import { omit } from '@eturi/util'
import type { Lang } from '@op/services'
import { resetAction, truncateIdAction } from '@op/services'
import { keysToInt } from '@op/util'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit'
import isEmpty from 'lodash/isEmpty'
import without from 'lodash/without'
import { createSliceTransformer } from 'rtk-slice-transformer'
import type {
	AppRuleSortType,
	EmailVerifyType,
	GuardModalType,
	ManageRoutePath,
	OnboardingPageName,
} from '../types'

export type AppMiscState = {
	readonly appRulesSortType: AppRuleSortType
	readonly canShowMenuView: boolean
	readonly didShowPostSignUp: boolean
	readonly emailVerifyType: Maybe<EmailVerifyType>
	readonly guardModalType: Maybe<GuardModalType>
	readonly isTrialBannerVisible: boolean
	readonly isWelcomeModalVisible: boolean
	readonly manageFeature: ManageRoutePath
	readonly momentLocale: Lang
	readonly scrollFrozenIds: string[]
	readonly selectedNotifId: Maybe<string>
	readonly showMobileManageTabs: boolean
	readonly showPagePadding: boolean
	readonly wasAllowanceWelcomeModalShown: boolean
	readonly wasVewInstructionsModalShown: boolean
	readonly onboardingCurrentPage: OnboardingPageName
	readonly onboardingIsComplete: boolean | null
}

export type WithAppMiscState = {
	readonly appMisc: AppMiscState
}

const initialState: AppMiscState = {
	appRulesSortType: 'alphabetical',
	canShowMenuView: false,
	// This is only set to false if we do actually sign up.
	didShowPostSignUp: true,
	emailVerifyType: null,
	guardModalType: null,
	isTrialBannerVisible: false,
	isWelcomeModalVisible: false,
	manageFeature: 'schedule',
	momentLocale: 'en',
	scrollFrozenIds: [],
	selectedNotifId: null,
	showMobileManageTabs: false,
	showPagePadding: true,
	wasAllowanceWelcomeModalShown: false,
	wasVewInstructionsModalShown: false,
	onboardingCurrentPage: 'concerns',
	onboardingIsComplete: null,
}

export const appMiscSlice = createSlice({
	name: 'appMisc',
	initialState,
	reducers: {
		_addScrollFrozenId(s, a: PayloadAction<string>) {
			s.scrollFrozenIds.push(a.payload)
		},

		_removeScrollFrozenId(s, a: PayloadAction<string>) {
			s.scrollFrozenIds = without(s.scrollFrozenIds, a.payload)
		},

		setAppRulesSortType(s, a: PayloadAction<AppRuleSortType>) {
			s.appRulesSortType = a.payload
		},

		setActiveManageFeature(s, a: PayloadAction<ManageRoutePath>) {
			s.manageFeature = a.payload
		},

		setAllowanceWelcomeModalShown(s, a: PayloadAction<boolean>) {
			s.wasAllowanceWelcomeModalShown = a.payload
		},

		setCanShowMenuView(s, a: PayloadAction<boolean>) {
			s.canShowMenuView = a.payload
		},

		setEmailVerify(s, a: PayloadAction<Maybe<EmailVerifyType>>) {
			s.emailVerifyType = a.payload
		},

		setGuardModal(s, a: PayloadAction<Maybe<GuardModalType>>) {
			s.guardModalType = a.payload
		},

		setMobileManageTabsVisible(s, a: PayloadAction<boolean>) {
			s.showMobileManageTabs = a.payload
		},

		setMomentLocale(s, a: PayloadAction<Lang>) {
			s.momentLocale = a.payload
		},

		setOnboardingPage(s, { payload: page }: PayloadAction<OnboardingPageName>) {
			s.onboardingCurrentPage = page
		},

		setOnboardingComplete(s, { payload }: PayloadAction<boolean>) {
			s.onboardingIsComplete = payload
		},

		setPagePaddingVisible(s, a: PayloadAction<boolean>) {
			s.showPagePadding = a.payload
		},

		setSelectedNotifId(s, a: PayloadAction<Maybe<string>>) {
			s.selectedNotifId = a.payload
		},

		setTrialBannerVisible(s, a: PayloadAction<boolean>) {
			s.isTrialBannerVisible = a.payload
		},

		setVewInstructionsModalShown(s, a: PayloadAction<boolean>) {
			s.wasVewInstructionsModalShown = a.payload
		},

		setWelcomeModalVisible(s, a: PayloadAction<boolean>) {
			s.isWelcomeModalVisible = a.payload
		},
	},
	extraReducers: (builder) => builder.addCase(resetAction, () => initialState),
})

export const {
	setActiveManageFeature,
	setAllowanceWelcomeModalShown,
	setAppRulesSortType,
	setCanShowMenuView,
	setEmailVerify,
	setGuardModal,
	setMobileManageTabsVisible,
	setMomentLocale,
	setPagePaddingVisible,
	setSelectedNotifId,
	setTrialBannerVisible,
	setVewInstructionsModalShown,
	setWelcomeModalVisible,
	setOnboardingPage,
	setOnboardingComplete,
} = appMiscSlice.actions

const isTruncatedIdAction = isAnyOf(
	appMiscSlice.actions._addScrollFrozenId,
	appMiscSlice.actions._removeScrollFrozenId,
)

export const appMiscSliceTransformer = /*@__PURE__*/ createSliceTransformer(
	appMiscSlice,
	(s) => ({
		...omit(s, ['momentLocale']),
		...keysToInt(s, ['scrollFrozenIds']),
	}),
	(a) => {
		if (setGuardModal.match(a)) return a
		if (isTruncatedIdAction(a)) return truncateIdAction(a)

		return null
	},
)

////////// Selectors ///////////////////////////////////////////////////////////
const state$ = <T extends WithAppMiscState>(s: T) => s.appMisc

export const appRulesSortType$ = /*@__PURE__*/ createSelector(state$, (s) => s.appRulesSortType)
export const canShowMenuView$ = /*@__PURE__*/ createSelector(state$, (s) => s.canShowMenuView)
export const emailVerifyType$ = /*@__PURE__*/ createSelector(state$, (s) => s.emailVerifyType)
export const guardModalType$ = /*@__PURE__*/ createSelector(state$, (s) => s.guardModalType)
export const isPagePaddingVisible$ = /*@__PURE__*/ createSelector(state$, (s) => s.showPagePadding)
export const isScrollFrozen$ = /*@__PURE__*/ createSelector(
	state$,
	(s) => !isEmpty(s.scrollFrozenIds),
)
export const isTrialBannerVisible$ = /*@__PURE__*/ createSelector(
	state$,
	(s) => s.isTrialBannerVisible,
)
export const isWelcomeModalVisible$ = /*@__PURE__*/ createSelector(
	state$,
	(s) => s.isWelcomeModalVisible,
)
export const manageFeature$ = /*@__PURE__*/ createSelector(state$, (s) => s.manageFeature)
export const momentLocale$ = /*@__PURE__*/ createSelector(state$, (s) => s.momentLocale)
export const onboardingCurrentPage$ = /*@__PURE__*/ createSelector(
	state$,
	(s) => s.onboardingCurrentPage,
)
export const onboardingIsComplete$ = /*@__PURE__*/ createSelector(
	state$,
	(s) => s.onboardingIsComplete,
)
export const selectedNotifId$ = /*@__PURE__*/ createSelector(state$, (s) => s.selectedNotifId)
export const showMobileManageTabs$ = /*@__PURE__*/ createSelector(
	state$,
	(s) => s.showMobileManageTabs,
)
export const wasAllowanceWelcomeModalShown$ = /*@__PURE__*/ createSelector(
	state$,
	(s) => s.wasAllowanceWelcomeModalShown,
)
export const wasVewInstructionsModalShown$ = /*@__PURE__*/ createSelector(
	state$,
	(s) => s.wasVewInstructionsModalShown,
)
